import React from "react";
import { X, Check } from "lucide-react";

const withoutPoints = [
  "Missed calls and lost leads after hours",
  "Manual follow-ups and ad-hoc workflows",
  "Generic agency templates and slow sites",
  "Tools that don't talk to each other",
  "No clear visibility into what's working",
  "Hiring more people to scale operations",
];

const withPoints = [
  "AI receptionist captures every call · 24/7",
  "Automated follow-ups, routing, and handoffs",
  "Premium websites engineered for conversion",
  "CRM, calendar, and channels in one system",
  "Live dashboards on leads, calls, and revenue",
  "Software that scales with you · not headcount",
];

const Bullet = ({ icon: Icon, color, children }) => (
  <li className="flex gap-3 items-start py-3 border-b border-white/5 last:border-0">
    <span
      className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0"
      style={{
        background: `${color}1f`,
        border: `1px solid ${color}40`,
        color,
      }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
    </span>
    <span className="text-sm text-zinc-300 leading-relaxed">{children}</span>
  </li>
);

export default function Comparison() {
  return (
    <section
      id="comparison"
      className="relative section-padding"
      data-testid="comparison-section"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14 reveal">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-[#39FF14]">
            <span className="w-6 h-px bg-[#39FF14]" />
            The Shift
            <span className="w-6 h-px bg-[#39FF14]" />
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            The old way is{" "}
            <span className="line-through decoration-zinc-700">leaking</span>{" "}
            money.
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Every missed call, every manual task, every disconnected tool is a
            tax on growth. We replace it with one intelligent system.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Without */}
          <div
            className="relative rounded-2xl border border-white/8 bg-[#0A0A0A] p-7 reveal"
            data-testid="comparison-without"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-zinc-500">
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <h3 className="text-lg font-semibold text-zinc-300">
                  Without Core Scale
                </h3>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 border border-white/10 px-2 py-1 rounded-full">
                Status quo
              </span>
            </div>
            <ul className="space-y-0">
              {withoutPoints.map((p) => (
                <Bullet key={p} icon={X} color="#71717a">
                  <span className="text-zinc-500">{p}</span>
                </Bullet>
              ))}
            </ul>
          </div>

          {/* With */}
          <div
            className="relative rounded-2xl p-7 reveal overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(57,255,20,0.06) 0%, rgba(10,10,10,1) 80%)",
              border: "1px solid rgba(57,255,20,0.25)",
              boxShadow: "0 0 60px rgba(57,255,20,0.06)",
            }}
            data-testid="comparison-with"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#39FF14]/20 blur-[100px] rounded-full pointer-events-none" />
            <div className="relative flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#39FF14]/15 border border-[#39FF14]/40 flex items-center justify-center text-[#39FF14]">
                  <Check className="w-4 h-4" strokeWidth={2.5} />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  With Core Scale
                </h3>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/30 px-2 py-1 rounded-full bg-[#39FF14]/8">
                Operational
              </span>
            </div>
            <ul className="relative space-y-0">
              {withPoints.map((p) => (
                <Bullet key={p} icon={Check} color="#39FF14">
                  <span className="text-white">{p}</span>
                </Bullet>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
